import React, { Ref, useImperativeHandle } from 'react';
import { cn } from '@/lib';
import { StanButton } from '@/components';
import { FileAttachmentPreview } from './FileAttachmentPreview';
import { QuickChatPrompts } from './QuickChatPrompts';
import { useChatInput } from '../hooks/useChatInput';
import { useFileAttachments } from '../hooks/useFileAttachments';
import { useAskStanley } from '../hooks/useAskStanley';
import IcArrowUp from '@/assets/icons/ic-arrow-up.svg';
import IcPlus from '@/assets/icons/ic-plus.svg';
import IcStop from '@/assets/icons/ic-stop-fill.svg';
import { FILE_ATTACHMENT_CONSTANTS } from '../constants/fileAttachments.constants';

interface ChatInputProps {
    showSuggestions: boolean;
    onPreviewImage: (file: File) => void;
}

export interface ChatInputHandle {
    focus: () => void;
}

export const ChatInput = ({
    showSuggestions,
    onPreviewImage,
    ref,
}: ChatInputProps & { ref?: Ref<ChatInputHandle> }) => {
    // Use hooks directly within the component
    const { messageText, inputHeight, textareaRef, handleMessageTextChange, resetInput, focusInput } = useChatInput();
    const {
        attachments,
        fileInputRef,
        getAcceptedFileTypes,
        removeAttachment,
        clearAttachments,
        openFileDialog,
        handleFileSelect,
        isLoading: isFileLoading,
    } = useFileAttachments();
    const { isLoading, isStreaming, sendMessage, abortCurrentRequest } = useAskStanley();

    // Expose imperative handle for parent to focus input
    useImperativeHandle(
        ref,
        () =>
            ({
                focus: focusInput,
            }) as ChatInputHandle
    );

    const canSend = (messageText.trim() || attachments.length > 0) && !isLoading && !isStreaming;
    const maxAttachmentsReached = attachments.length >= FILE_ATTACHMENT_CONSTANTS.DEFAULT_CONFIG.maxAttachments;

    const handleSendMessage = () => {
        if ((messageText.trim() || attachments.length > 0) && !isLoading && !isStreaming) {
            sendMessage(messageText.trim(), attachments);
            resetInput();
            clearAttachments();
            // Keep focus on input after sending
            focusInput();
        }
    };

    const handleSuggestionClick = (message: string) => {
        sendMessage(message, []);
        // Keep focus on input after sending suggestion
        focusInput();
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (canSend) {
            handleSendMessage();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Enter submits, Shift+Enter creates new line
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    return (
        <div className={cn('bg-brand-dimmed rounded-b-2xl p-4')}>
            <QuickChatPrompts showPrompts={showSuggestions} onSendMessage={handleSuggestionClick} />

            {/* File Input (Hidden) */}
            <input
                ref={fileInputRef}
                type='file'
                multiple
                accept={getAcceptedFileTypes()}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />

            {/* Input Field */}
            <form onSubmit={handleSubmit} className='bg-canvas border-brand-subtle rounded-lg border shadow-sm'>
                {/* Attachment Preview */}
                {attachments.length > 0 && (
                    <div className='p-3'>
                        <FileAttachmentPreview
                            attachments={attachments}
                            onRemoveAttachment={removeAttachment}
                            onPreviewImage={onPreviewImage}
                            isLoading={isFileLoading}
                            size='xs'
                        />
                    </div>
                )}

                <div className='p-3'>
                    <textarea
                        ref={textareaRef}
                        value={messageText}
                        onChange={e => handleMessageTextChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isLoading || isStreaming}
                        placeholder='Ask Stanley...'
                        className={cn(
                            'body-md text-dark placeholder-gray w-full resize-none border-none bg-transparent focus:outline-none',
                            isLoading || isStreaming ? 'cursor-not-allowed opacity-60' : ''
                        )}
                        style={{ height: `${inputHeight}px` }}
                    />
                </div>
                <div className='flex items-center justify-between px-3 pb-3'>
                    <StanButton
                        name='add-attachment'
                        type='button'
                        onClick={openFileDialog}
                        disabled={maxAttachmentsReached || isLoading || isStreaming}
                        color='primary'
                        variant='ghost'
                        size='sm'
                        icon={IcPlus}
                        title={maxAttachmentsReached ? 'Maximum 10 files allowed' : 'Add files'}
                        aria-label='Add attachment'
                    />

                    {(isStreaming || isLoading) && (
                        <StanButton
                            name='stop-streaming'
                            type='button'
                            onClick={abortCurrentRequest}
                            color='primary'
                            size='sm'
                            icon={IcStop}
                            aria-label='Stop streaming'
                        />
                    )}
                    {!(isStreaming || isLoading) && (
                        <StanButton
                            name='send-message'
                            type='submit'
                            disabled={!canSend}
                            color={canSend ? 'primary' : 'secondary'}
                            size='sm'
                            icon={IcArrowUp}
                            aria-label='Send message'
                        />
                    )}
                </div>
            </form>
        </div>
    );
};

ChatInput.displayName = 'ChatInput';
